import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container, Table, Col, Row, Button,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import moment from "moment";
import { fetchReport, fetchWallets } from "store/actions";
import CardWrapper from "components/Common/CardWrapper";
import CustomPagination from "components/Common/CustomPagination";
import { MetaTags } from "react-meta-tags";
import {
  Th,
  Thead,
  Tr
} from "react-super-responsive-table";
import Loader from "components/Common/Loader";
import DownloadReportButton from "./DownloadReportButton";
import { downloadWalletReportAPI } from "apis/forex/reports";

const Transfers = (props) => {
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const { wallets, loading: walletsLoading } = useSelector((state) => state.walletReducer);
  const { data, loading, pagination } = useSelector((state) => state.walletReducer.report);
  const [selectedTransactionType, setSelectedTransactionType] = useState("");
  const [selectedWallet, setSelectedWallet] = useState("");

  const getReportPayload = (page = 1, limit = sizePerPage, values = {}) => {
    const type = values.type ?? selectedTransactionType;
    const currency = type === "CASHBACK" ? "" : (values.currency ?? selectedWallet);
    const obj = {
      page,
      limit,
      dateFrom,
      dateTo,
      currency: currency || "ALL",
    };
    obj.type = type || "ALL";
    return obj;
  };

  const getReport = (page = 1, limit = sizePerPage, v = {}) => {
    const obj = getReportPayload(page, limit, v);
    dispatch(fetchReport(obj));
  };

  useEffect(() => {
    getReport(1, sizePerPage);
  }, [sizePerPage]);

  const getStatusColor = (type) => {
    switch (String(type).toLowerCase()) {
      case "approved":
        return "color-green";
      case "rejected":
        return "color-red";
      default:
        return "color-gray";
    }
  };

  const getWalletLabel = (item) => {
    if (item?.walletId?.isIb) return "IB";
    if (item?.type) return item?.currency;

    const baseCurrency = item?.fromId?.isIb ? "IB" : item?.baseCurrency;
    const targetCurrency = item?.toId?.isIb ? "IB" : item?.targetCurrency;
    return `${baseCurrency} -> ${targetCurrency}`;
  };

  const columns = [
    {
      dateField: "createdAt",
      text: props.t("Date"),
      formatter: (val) => moment(val?.createdAt).format("DD MMM YYYY HH:mm:ss"),
    },
    {
      dateField: "type",
      text: props.t("Type"),
      formatter: (val) => <>{val?.type || "INTERNAL_TRANSFER"}</>,
    },
    {
      dateField: "currency",
      text: props.t("Wallet"),
      formatter: (val) => getWalletLabel(val)
    },
    {
      dateField: "gateway",
      text: props.t("Gateway"),
      formatter: (val) => <>{val?.gateway || "INTERNAL_TRANSFER"}</>,
    },
    {
      dateField: "amount",
      text: props.t("Amount"),
      formatter: (val) => {
        return <span className="font-weight-bold color-green">{val?.amount?.  $numberDecimal || val?.amount || "-"}</span>;
      }
    },
    {
      dataField: "status",
      text: props.t("Status"),
      formatter: (val) => <span className={`${getStatusColor(val?.status)} text-capitalize font-weight-bold`}>{val?.status}</span>,
    },
  ];

  useEffect(() => {
    dispatch(fetchWallets());
  }, []);


  return (
    <Container>
      <div className="page-content mt-5">
        <MetaTags>
          <title>{props.t("Reports")}</title>
        </MetaTags>
        <CardWrapper className="mt-3 px-5 py-4 glass-card shadow">
          <div className="d-flex justify-content-between">
            <h3 className="color-primary">{props.t("Wallet Report")}</h3>
            <DownloadReportButton
              disabled={loading || !data?.length}
              filename="wallet-report"
              getPayload={() => getReportPayload()}
              onDownload={downloadWalletReportAPI}
            />
          </div>
          <AvForm onValidSubmit={(e, v) => getReport(1, sizePerPage, v)}>
            <Row className="mt-3  align-items-end">
              <Col xs="12" md="3">
                <AvField type="select" name="type" label={props.t("Transaction Type")} className="mt-1 mb-2 form-select" onChange={(e) => {
                  setSelectedTransactionType(e.target.value);
                  if (e.target.value === "CASHBACK") setSelectedWallet("");
                }}>
                  <option value="">{props.t("All")}</option>
                  {<option value="DEPOSIT">{props.t("Deposits")}</option>}
                  <option value="WITHDRAW">{props.t("Withdraws")}</option>
                  <option value="TRANSFER">{props.t("Internal Transfers")}</option>
                  <option value="CASHBACK">{props.t("Cashbacks")}</option>
                </AvField>
              </Col>
              { selectedTransactionType !== "CASHBACK" ? <Col xs="12" md="3">
                {walletsLoading ? (
                  <Loader />
                ) :  (
                  <AvField type="select" name="currency" label={props.t("Wallets")} className="mt-1 mb-2 form-select" onChange={(e) => setSelectedWallet(e.target.value)}>
                    <option value="">{props.t("All")}</option>
                    {wallets?.filter(wallet => !wallet.isInventory).map((wallet) => (
                      <option key={wallet.asset} value={wallet.asset}>
                        {wallet.asset}
                      </option>
                    ))}
                  </AvField>
                )}
              </Col> : null}
              <Col xs="12" md="3" lg="2">
                <AvField type="date" name="dateFrom" label={props.t("Date From")} className="mt-1 mb-2"
                  onChange={(e) => { setDateFrom(e.target.value) }}
                  max={dateTo ? moment(dateTo).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}
                  required />
              </Col>
              <Col xs="12" md="3" lg="2">
                <AvField type="date" name="dateTo" label={props.t("Date To")} className="mt-1 mb-2"
                  onChange={(e) => { setDateTo(e.target.value) }}
                  max={moment().add(1, "days").format("YYYY-MM-DD")}
                  min={dateFrom && moment(dateFrom).format("YYYY-MM-DD")}
                  required />
              </Col>
              <Col xs="12" md="3" lg="2" >
                <Button type="submit" className="border-0 color-bg-btn mb-md-2 mt-3 mt-lg-0 w-100">{props.t("Submit")}</Button>
              </Col>
            </Row>
          </AvForm>
          <hr className="my-4" />
          <div className="mt-4 border rounded-3">
            <Table borderless responsive className="text-center mb-0" >
              <Thead className="text-center table-light">
                <Tr>
                  {columns.map((column, index) => (
                    <Th data-priority={index} key={index} className="color-primary"> 
                      {column.text}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <tbody>
                {loading ? (
                  <Loader />
                ) : (
                  data?.map((item, index) => (
                    <Tr key={index}>
                      {columns.map((column, index) => (
                        <td key={index} data-priority={index}>
                          {column.formatter ? column.formatter(item) : item[column.dateField]}
                        </td>
                      ))}
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          <div className="mt-4">
            <CustomPagination
              {...pagination}
              page={parseInt(pagination.page)}
              limit={parseInt(pagination.limit)}
              setSizePerPage={setSizePerPage}
              sizePerPage={sizePerPage}
              onChange={getReport}
            />
          </div>
        </CardWrapper>
      </div>
    </Container>
  );
};

export default withTranslation()(Transfers);
